import React from 'react';
import T from 'i18n-react';
import type { NextPage, NextPageContext } from 'next';

import MetaTags from 'Components/MetaTags/MetaTags';
import PageComposer from 'Components/PageComposer/PageComposer';
import CustomSignupPage from 'Components/SignupPage/SignupPage';
import texts from 'Components/texts.json';
import { getMakes } from 'Services/api/vehicles';
import { PAGE_SLUGS } from 'Utilities/consts';
import type {
	GetInitialPropsMiddleware,
	GetInitialPropsMiddlewareContext,
} from 'Utilities/getInitialPropsMiddlewares/getInitialPropsMiddlewares.types';
import { unauthorisedOnly } from 'Utilities/getInitialPropsMiddlewares/unauthorisedOnly/unauthorisedOnly';
import { createMiddlewarePipeline } from 'Utilities/helpers/middlewarePipeline/middlewarePipeline';

T.setTexts(texts);

type SignupPageProps = {
	makes?: {
		text: string;
		value: number;
	}[];
};

const SignupPage: NextPage<SignupPageProps> = ({ makes }) => (
	<>
		<MetaTags
			// @ts-ignore
			includeCanonicalTag
			description={T.translate('metaTags.pages.signUp.description')}
			ogDescription={T.translate('metaTags.pages.signUp.description')}
			ogTitle={T.translate('metaTags.pages.signUp.title')}
			title={T.translate('metaTags.pages.signUp.title')}
		/>

		<PageComposer pageSlug={PAGE_SLUGS.SIGN_UP}>
			<CustomSignupPage makes={makes} />
		</PageComposer>
	</>
);

const getInitialPropsHandler: GetInitialPropsMiddleware<NextPageContext, SignupPageProps> = async (context) => {
	const makes = await getMakes(context.nextContext);

	context.returnValue.makes = makes.map((m) => ({ text: m.name, value: m.id }));
};

export const signUpGetInitialProps = async (context: NextPageContext): Promise<SignupPageProps> => {
	type MiddlewarePipelineContext = GetInitialPropsMiddlewareContext<typeof context, SignupPageProps>;

	const middlewarePipeline = createMiddlewarePipeline<MiddlewarePipelineContext>(
		unauthorisedOnly,
		getInitialPropsHandler,
	);

	const getInitialPropsMiddlewareContext: MiddlewarePipelineContext = {
		nextContext: context,
		returnValue: {},
	};

	await middlewarePipeline.run(getInitialPropsMiddlewareContext);

	return getInitialPropsMiddlewareContext.returnValue;
};

SignupPage.getInitialProps = signUpGetInitialProps;

export default SignupPage;
